/**
 * Single source of truth for quote/order status.
 * Used for display labels, badge colors, filters, and status updates.
 */

/** Labels describe current state (not the next CTA). */
export const QUOTE_STATUS_OPTIONS = [
  {
    value: '0_building_quote',
    label: 'Draft || Utkast',
    color: 'secondary',
  },
  {
    value: '1_contacted_sales',
    /** Customer asked Klayder to review (checkout CTA or chat ping). */
    label: 'In review || Under granskning',
    color: 'yellow',
  },
  {
    value: '1_5_sales_approved_quote',
    /** Klayder signed off; waiting for customer to approve in Checkout. */
    label: 'Ready to approve || Redo att godkänna',
    color: 'orange',
  },
  {
    value: '2_approved_quote',
    /** Default when no factory proofs yet; see `getQuoteStatusDisplayLabel`. */
    label: 'Awaiting proofs || Väntar på korrekturer',
    color: 'orange',
  },
  {
    value: '3_approved_proofs',
    label: 'Proof approved || Korrektur godkänd',
    color: 'blue',
  },
  {
    value: '4_started_production',
    label: 'In production || I produktion',
    color: 'purple',
  },
  {
    value: '5_awaiting_shipment',
    label: 'Shipping || Leverans',
    color: 'purple',
  },
  /** @deprecated Prefer `5_awaiting_shipment`. Kept for legacy quote rows. */
  {
    value: 'awaiting_shipment',
    label: 'Shipping || Leverans',
    color: 'purple',
  },
  {
    value: '8_delivered',
    label: 'Delivered || Levererad',
    color: 'green',
  },
] as const

/** Earliest pipeline step; DB may store `null` before a status is set */
export const BUILDING_QUOTE_STATUS = '0_building_quote'

/** Customer asked Klayder to review the quote — still Explore */
export const CONTACTED_SALES_STATUS = '1_contacted_sales'

/**
 * Klayder (admin) approved the draft quote — customer may approve in Checkout.
 * Order table locks for non-admins (Checkout phase).
 */
export const SALES_APPROVED_QUOTE_STATUS = '1_5_sales_approved_quote'

/** Customer-approved quote — waiting on / reviewing factory proofs */
export const APPROVED_QUOTE_STATUS = '2_approved_quote'

/** Customer approved factory proofs */
export const APPROVED_PROOFS_STATUS = '3_approved_proofs'

/** Production started; Delivery unlocks */
export const STARTED_PRODUCTION_STATUS = '4_started_production'

/** Canonical shipping status; legacy quote rows may still use `awaiting_shipment` */
export const SHIPPING_QUOTE_STATUS = '5_awaiting_shipment'

/** Statuses selectable in admin / progress UI (excludes legacy shipping alias) */
export const SELECTABLE_QUOTE_STATUS_OPTIONS = QUOTE_STATUS_OPTIONS.filter(
  (o) => o.value !== 'awaiting_shipment'
)

/** Single place to treat missing status as “build quote” / draft */
export function effectiveQuoteStatus(
  status: string | null | undefined
): string {
  if (status == null || status === '') return BUILDING_QUOTE_STATUS
  // Collapse legacy quote-level shipping alias for phase lookup / display grouping
  if (status === 'awaiting_shipment') return SHIPPING_QUOTE_STATUS
  return status
}

/** Orders “Drafts” segment: `null` or explicit building-quote status */
export function isDraftQuoteStatus(status: string | null | undefined): boolean {
  return effectiveQuoteStatus(status) === BUILDING_QUOTE_STATUS
}

/** Draft or in-review — before Klayder releases the quote for customer approval */
export function isPreSalesApprovalStatus(
  status: string | null | undefined
): boolean {
  const effective = effectiveQuoteStatus(status)
  return (
    effective === BUILDING_QUOTE_STATUS ||
    effective === CONTACTED_SALES_STATUS
  )
}

/** Customer asked Klayder to review — waiting on sales to release (`1_5`) */
export function isAwaitingKlayderReview(
  status: string | null | undefined
): boolean {
  return effectiveQuoteStatus(status) === CONTACTED_SALES_STATUS
}

export function getQuoteStatusOption(status: string | null | undefined) {
  return QUOTE_STATUS_OPTIONS.find(
    (o) => o.value === effectiveQuoteStatus(status)
  )
}

/** Customer label when status is `2` and factory proofs are attached. */
export const AWAITING_APPROVAL_LABEL =
  'Awaiting approval || Väntar på godkännande'

/**
 * Customer-facing badge/sub-label. For `2_approved_quote`:
 * - no proofs yet → "Awaiting proofs"
 * - proofs attached → "Awaiting approval"
 */
export function getQuoteStatusDisplayLabel(
  status: string | null | undefined,
  { hasProofs = false }: { hasProofs?: boolean } = {}
): string {
  const effective = effectiveQuoteStatus(status)
  if (effective === APPROVED_QUOTE_STATUS && hasProofs) {
    return AWAITING_APPROVAL_LABEL
  }
  return getQuoteStatusOption(status)?.label ?? 'Draft || Utkast'
}

/**
 * Customer-facing pipeline phases for progress UI.
 *
 * 1. Explore — draft / in review
 * 2. Checkout — Klayder-ready quote → customer approve → proofs → details
 * 3. Delivery — production / shipping (delivered marks all steps done)
 *
 * Status `1_5` finishes Explore and starts Checkout (customer can approve).
 * Status `2` is customer-approved (waiting on factory proofs).
 * Asking Klayder to approve (`1` In review) stays under Explore.
 */
export const QUOTE_STATUS_PHASES = [
  {
    key: 'explore',
    label: 'Explore || Utforska',
    description:
      'Find products and designs, build the draft quote || Hitta produkter och designs, bygg utkastet',
    statuses: [BUILDING_QUOTE_STATUS, CONTACTED_SALES_STATUS],
  },
  {
    key: 'checkout',
    label: 'Checkout || Kassa',
    description:
      'Approve quote and proofs, then place order || Godkänn offert och korrekturer, sedan lägg order',
    statuses: [
      SALES_APPROVED_QUOTE_STATUS,
      APPROVED_QUOTE_STATUS,
      APPROVED_PROOFS_STATUS,
    ],
  },
  {
    key: 'delivery',
    label: 'Delivery || Leverans',
    description: 'Production, shipping, and invoice || Produktion, leverans och faktura',
    statuses: [STARTED_PRODUCTION_STATUS, SHIPPING_QUOTE_STATUS],
  },
] as const

/** Status value for delivered orders – used to split Orders vs History */
export const DELIVERED_STATUS = '8_delivered'

export function getQuoteStatusPhaseIndex(
  status: string | null | undefined
): number {
  const effective = effectiveQuoteStatus(status)
  if (effective === DELIVERED_STATUS) {
    return QUOTE_STATUS_PHASES.length
  }
  const index = QUOTE_STATUS_PHASES.findIndex((phase) =>
    (phase.statuses as readonly string[]).includes(effective)
  )
  return index === -1 ? 0 : index
}

export function getQuoteStatusPhase(status: string | null | undefined) {
  return QUOTE_STATUS_PHASES[getQuoteStatusPhaseIndex(status)]
}

/**
 * Admin status menu groups: main phase label + selectable sub-statuses.
 * Delivered is selectable under Complete.
 */
export const ADMIN_QUOTE_STATUS_GROUPS = QUOTE_STATUS_PHASES.map((phase) => ({
  key: phase.key,
  label: phase.label,
  statuses:
    phase.key === 'delivery'
      ? ([...phase.statuses, DELIVERED_STATUS] as const)
      : phase.statuses,
}))

/** Active pipeline statuses (excludes draft + delivered) — prefer `.in()` filters */
export const ACTIVE_QUOTE_STATUSES = QUOTE_STATUS_OPTIONS.filter(
  (o) => o.value !== BUILDING_QUOTE_STATUS && o.value !== DELIVERED_STATUS
).map((o) => o.value)

export const SHIPMENT_STATUS_OPTIONS = [
  {
    value: 'awaiting_shipment',
    label: 'Awaiting shipment || Väntar på leverans',
    color: 'purple',
  },
  {
    value: 'in_transit',
    label: 'In transit || På väg',
    color: 'blue',
  },
  {
    value: 'out_for_delivery',
    label: 'Out for delivery || Ute för leverans',
    color: 'blue',
  },
  {
    value: 'delivered',
    label: 'Delivered || Levererad',
    color: 'green',
  },
] as const
